import { FC, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import InputNumber from 'modules/_shared/components/InputNumber';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';

import { useRenumerationModal } from '../context/RenumerationModalContext';
import { CustomRange } from '../types/CustomRange';

type RangeRowProps = {
  index: number;
  range: CustomRange;
};

const RangeRow: FC<RangeRowProps> = ({ index, range }: RangeRowProps) => {
  const { t } = useTranslate();
  const { data, actions } = useRenumerationModal();

  const [isEditing, setIsEditing] = useState(false);
  const [draftFrom, setDraftFrom] = useState<number>(
    range.newFrom || range.from
  );
  const [draftTo, setDraftTo] = useState<number>(range.newTo || range.to);

  const isEntireClass = data.isEntireClassRenumeration;

  const saved = data.savedRanges.find((r) => r.key === range.key);
  const isSaved = saved != null;

  const displayFrom = saved?.newFrom ?? range.newFrom;
  const displayTo = saved?.newTo ?? range.newTo;
  const isReassigned = displayFrom != null && displayTo != null;

  const getError = (): string | null => {
    if (Number.isNaN(draftFrom) || Number.isNaN(draftTo)) return 'InvalidRange';
    if (draftFrom > draftTo) return 'InvalidRange';
    const sourceCount = range.to - range.from + 1;
    const targetCount = draftTo - draftFrom + 1;
    if (targetCount !== sourceCount) return 'CountMismatch';
    return null;
  };

  const error = isEditing ? getError() : null;
  const errorId = `range-error-${index}`;

  const handleStartEdit = () => {
    setDraftFrom(range.newFrom || range.from);
    setDraftTo(range.newTo || range.to);
    setIsEditing(true);
  };

  const handleDone = () => {
    if (getError()) return;
    actions.handleEditRange(range.key, range, draftFrom, draftTo);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraftFrom(range.newFrom || range.from);
    setDraftTo(range.newTo || range.to);
    setIsEditing(false);
  };

  return (
    <div className="range-row row gx-1 align-items-center">
      <div className="col-2">
        <span className="range-row__value">{formatNumber(range.from)}</span>
      </div>

      <div className="col-2">
        <span className="range-row__value">{formatNumber(range.to)}</span>
      </div>

      {isEditing && !isSaved ? (
        <>
          <div className="col-2">
            <div
              className={`range-row__field${
                error ? ' range-row__field--error' : ''
              }`}
            >
              <InputNumber
                id={`new-from-${index}`}
                value={draftFrom}
                onChange={setDraftFrom}
                lgSize={false}
                required
              />
            </div>
          </div>

          <div className="col-2">
            <div
              className={`range-row__field${
                error ? ' range-row__field--error' : ''
              }`}
            >
              <InputNumber
                id={`new-to-${index}`}
                value={draftTo}
                onChange={setDraftTo}
                lgSize={false}
                required
              />
            </div>
            {error && (
              <IconTooltip
                id={errorId}
                icon="alert-circle"
                tooltip={t(error)}
                wrapperClassName="range-row__error-icon"
                className="text-danger"
              />
            )}
          </div>

          <div className="col-4 range-row__actions">
            <Button text={t('Done')} onClick={handleDone} disabled={!!error} />
            <Button
              variant={ButtonVariants.LINK}
              text={t('Cancel')}
              onClick={handleCancel}
            />
          </div>
        </>
      ) : (
        <>
          {isReassigned ? (
            <>
              <div className="col-2">
                <InputNumber
                  id={`view-from-${index}`}
                  value={displayFrom as number}
                  onChange={() => {}}
                  lgSize={false}
                  disabled
                />
              </div>

              <div className="col-2">
                <InputNumber
                  id={`view-to-${index}`}
                  value={displayTo as number}
                  onChange={() => {}}
                  lgSize={false}
                  disabled
                />
              </div>
            </>
          ) : (
            <div className="col-4">
              <span className="range-row__placeholder">
                {t('NotReassigned')}
              </span>
            </div>
          )}

          <div className="col-4 range-row__actions">
            {!isEntireClass && !isSaved && (
              <Button text={t('Edit')} onClick={handleStartEdit} />
            )}
          </div>
        </>
      )}
    </div>
  );
};
export default RangeRow;
